import { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { useDeletePageTemplate } from '../../../../api/pages-client/pages-client.hooks';
import { PageTemplate } from '../../../../api/pages-client/pages-client.type';
import { BrandedLoadingOverlay } from '../../../../components/loader/branded-loader';
import { DeleteConfirmationModal } from '../../../data-management/components/delete-confirmation-modal';
import { DashboardListGrid } from '../dashboards-list/dashboards-list';
import { EmptyStateBox } from '../empty-state-box';
import { DashboardTemplateListItem } from './dashboard-template-list-item';
import { DashboardTemplateModal } from './dashboard-template-modal';

type Props = {
  pageTemplates: PageTemplate[];
  pageTemplatesQuery: UseQueryResult<PageTemplate[], Error>;
};
export const DashboardTemplateList = ({ pageTemplates, pageTemplatesQuery }: Props) => {
  const [isEditTemplateOpen, setIsEditTemplateOpen] = useState(false);
  const [isDeleteTemplateOpen, setIsDeleteTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PageTemplate | null>(null);

  const deletePageTemplateMutation = useDeletePageTemplate();

  const handleEditTemplate = (template: PageTemplate) => {
    setSelectedTemplate(template);
    setIsEditTemplateOpen(true);
  };

  const handleDeleteTemplate = (template: PageTemplate) => {
    setSelectedTemplate(template);
    setIsDeleteTemplateOpen(true);
  };

  const onDeleteTemplate = () => {
    if (selectedTemplate?.id) {
      try {
        deletePageTemplateMutation.mutate(selectedTemplate.id);
      } catch (error) {
        console.error(error);
      }
    }
    setSelectedTemplate(null);
    setIsDeleteTemplateOpen(false);
  };

  if (!pageTemplatesQuery.isLoading && pageTemplates.length === 0) {
    return (
      <EmptyStateBox
        title="No Templates Found"
        icon="info"
        description="Create a template from a dashboard by clicking the 'Save as Template' button on a dashboard."
      />
    );
  }

  return (
    <>
      <BrandedLoadingOverlay visible={pageTemplatesQuery.isLoading} transitionDuration={30} variant="colored" />
      <DashboardListGrid>
        {pageTemplates.map((pageTemplate) => (
          <DashboardTemplateListItem
            key={pageTemplate.id}
            template={pageTemplate}
            onEdit={handleEditTemplate}
            onDelete={handleDeleteTemplate}
          />
        ))}
      </DashboardListGrid>
      <DashboardTemplateModal
        opened={isEditTemplateOpen}
        onClose={() => {
          setIsEditTemplateOpen(false);
          setSelectedTemplate(null);
        }}
        onSuccess={() => {
          pageTemplatesQuery.refetch();
        }}
        initialData={{
          id: selectedTemplate?.id,
          name: selectedTemplate?.name || '',
          description: selectedTemplate?.description || '',
        }}
        isEditing={isEditTemplateOpen}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteTemplateOpen}
        onClose={() => {
          setSelectedTemplate(null);
          setIsDeleteTemplateOpen(false);
        }}
        onDelete={onDeleteTemplate}
        title="Delete DashboardTemplate"
        itemName={selectedTemplate?.name || ''}
        isDeleting={deletePageTemplateMutation.isPending}
        error={deletePageTemplateMutation.error}
      />
    </>
  );
};
